/**
 * Pure formatting and validation logic for the Send Queue tab (GFG-194, unit U7) — no React, no
 * I/O. Every function that cares about the current instant takes `now` as a parameter rather than
 * reading the clock itself, so behaviour is fully exercised by `sendQueue-helper.test.ts` without
 * faking `Date.now`. UI strings come from `constants/sendQueueCampaigns.ts`; wire/domain shapes come from
 * `interfaces/sendQueueCampaigns.ts` — neither is redefined here.
 */
import type { AppliedFilter } from "@/components/BlotterFilterBar";
import { CAMPAIGN_STATUSES } from "@/constants/campaings";
import { DATE_LOCALE } from "@/constants/dates";
import { COPY, SEND_SUB_LABELS } from "@/constants/sendQueueCampaigns";
import {
  type ReminderBasis,
  SEND_QUEUE_CAMPAIGN_TYPES,
  SEND_QUEUE_KINDS,
  SEND_QUEUE_SORT_KEYS,
  SEND_QUEUE_STATUSES,
  type SendQueueCampaignType,
  type SendQueueItem,
  type SendQueueKind,
  type SendQueueListParams,
  type SendQueueStatus,
  type SendQueueWindow,
} from "@/interfaces/sendQueueCampaigns";

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/** Days added to `now` for each numbered window; "all" has no end and is handled separately. */
const WINDOW_DAYS: Record<Exclude<SendQueueWindow, "all">, number> = { "1": 1, "7": 7, "30": 30 };

/**
 * "Sending now" while the send is in flight; "Due now" once the scheduled moment has arrived but
 * the row hasn't flipped to Sending yet (the API keeps a just-passed row in "Scheduled" for a
 * grace window — see sendQueueService's UPCOMING_GRACE_MS — so `now` can legitimately be past
 * `iso` here); otherwise a delta from `now` to `iso` — minutes under an hour, hours under a day
 * (singular at exactly one hour), "Tomorrow" for the next full day out, and "in N days" beyond
 * that. Deliberately elapsed-time based rather than calendar-day based: a 23-hour delta reads in
 * hours even though it usually lands on the next calendar date.
 */
export function formatRelative(iso: string, now: number, status?: SendQueueStatus): string {
  if (status === CAMPAIGN_STATUSES.QUEUE_SENDING) return "Sending now";

  const deltaMs = new Date(iso).getTime() - now;

  // Round into a unit first, then branch on the ROUNDED value — the same number that gets
  // printed is the one compared against each tier boundary. This is what keeps a delta that
  // rounds up to a boundary (e.g. 59m45s -> 60 min) from being both compared as the lower tier
  // and printed as the boundary value of the next one.
  const minutes = Math.round(deltaMs / MINUTE_MS);

  // Covers both directions of "there is no meaningful minute left": an `iso` at or before `now`
  // (deltaMs <= 0), and an `iso` up to ~29s in the future that still rounds down to 0 minutes.
  // "in 0 min" is a technically-valid "in N min" string but reads like a stalled countdown for a
  // moment that has, for all practical purposes, arrived — so both fold into the same label
  // rather than one becoming a negative count and the other a confusing zero.
  if (minutes <= 0) {
    return COPY.dueNow;
  }
  if (minutes < 60) {
    return `in ${minutes} min`;
  }

  const hours = Math.round(deltaMs / HOUR_MS);
  if (hours < 24) {
    return `in ${hours} hour${hours === 1 ? "" : "s"}`;
  }

  const days = Math.round(deltaMs / DAY_MS);
  if (days < 2) {
    return "Tomorrow";
  }
  return `in ${days} days`;
}

/**
 * "Jun 15 · 9:05 AM" — date and time formatted separately (so they can be joined with an
 * interpunct rather than the locale's own comma) and rendered in the runtime's local timezone,
 * deliberately: a cross-org queue needs one consistent clock, and an admin in New York must not
 * read 9:05 AM for a send that actually leaves at 5:05 AM local.
 */
export function formatAbsolute(iso: string): string {
  const date = new Date(iso);
  const datePart = date.toLocaleString(DATE_LOCALE, { month: "short", day: "numeric" });
  const timePart = date.toLocaleString(DATE_LOCALE, { hour: "numeric", minute: "2-digit" });
  return `${datePart} · ${timePart}`;
}

/**
 * "Reminder (i of N)" for a reminder row, composed from its own `reminderIndex`/`reminderTotal`
 * so N is always the campaign's real reminder count. Every other kind's `sendLabel` is returned
 * unchanged.
 */
export function reminderLabel(item: SendQueueItem): string {
  if (item.kind === "reminder" && item.reminderIndex != null && item.reminderTotal != null) {
    return `Reminder (${item.reminderIndex} of ${item.reminderTotal})`;
  }
  return item.sendLabel;
}

/**
 * The secondary line under a row's primary send label. A reminder's sub-label depends on
 * `reminderBasis`; every other kind has one fixed description. Strings come from
 * `SEND_SUB_LABELS` in constants/sendQueueCampaigns.ts — never retyped here.
 */
export function sendSubLabel(item: SendQueueItem): string {
  if (item.kind === "reminder") {
    return SEND_SUB_LABELS.reminderBasis[item.reminderBasis as ReminderBasis];
  }
  return SEND_SUB_LABELS.kind[item.kind];
}

/** `now` plus the window's day count; "all" has no end, so it's positive infinity. */
export function windowEndMs(window: SendQueueWindow, now: number): number {
  if (window === "all") return Number.POSITIVE_INFINITY;
  return now + WINDOW_DAYS[window] * DAY_MS;
}

/** Strictly after `now` — a reschedule to the current instant is not "in the future". */
export function isFutureIso(iso: string, now: number): boolean {
  return new Date(iso).getTime() > now;
}

/**
 * Validates a proposed reschedule time: `null` when it's fine, otherwise the exact copy string
 * for the failing rule. A time that isn't strictly in the future is always invalid; a reminder
 * additionally can't be moved to on-or-after the gift's expiry.
 */
export function rescheduleError(item: SendQueueItem, iso: string, now: number): string | null {
  if (!isFutureIso(iso, now)) return COPY.reschedule.errors.pastTime;

  if (
    item.kind === "reminder" &&
    item.expiry &&
    new Date(iso).getTime() >= new Date(item.expiry).getTime()
  ) {
    return COPY.reschedule.errors.afterExpiry;
  }

  return null;
}

/** The slice of the Blotter's query state that maps onto the Send Queue's API params. */
interface SendQueueBlotterQuery {
  search: string;
  filters: AppliedFilter[];
  sortBy: string;
  sortDir: "asc" | "desc";
  page: number;
  limit: number;
}

/** Narrows a filter chip's raw string values down to the ones a known vocabulary accepts. */
function valuesIn<T extends string>(values: string[], vocabulary: readonly T[]): T[] {
  return values.filter((value): value is T => vocabulary.some((allowed) => allowed === value));
}

/**
 * Maps the Blotter's generic `{ search, filters, sortBy, sortDir, page, limit }` query state onto
 * `SendQueueListParams`, dropping anything empty (a blank search, a filter chip with no values, an
 * unrecognised sort key) instead of forwarding it to the API.
 */
export function toListParams(
  blotterQuery: SendQueueBlotterQuery,
  window: SendQueueWindow,
): SendQueueListParams {
  const valuesFor = (fieldId: string) =>
    blotterQuery.filters.find((f) => f.fieldId === fieldId)?.values ?? [];

  const campaignType = valuesIn<SendQueueCampaignType>(
    valuesFor("campaign_type"),
    SEND_QUEUE_CAMPAIGN_TYPES,
  );
  const sendType = valuesIn<SendQueueKind>(valuesFor("send_type"), SEND_QUEUE_KINDS);
  const status = valuesIn<SendQueueStatus>(valuesFor("status"), SEND_QUEUE_STATUSES);
  const accountLeadId = valuesFor("account_lead");
  const sortBy = SEND_QUEUE_SORT_KEYS.find((key) => key === blotterQuery.sortBy);
  const search = blotterQuery.search.trim();

  return {
    window,
    search: search || undefined,
    campaign_type: campaignType.length ? campaignType : undefined,
    send_type: sendType.length ? sendType : undefined,
    status: status.length ? status : undefined,
    account_lead_id: accountLeadId.length ? accountLeadId : undefined,
    sortBy,
    sortDir: blotterQuery.sortDir,
    page: blotterQuery.page,
    limit: blotterQuery.limit,
  };
}

/** One row's values for CSV export, in the ticket's exact column order. `scheduledAt` is the raw ISO string, not a formatted date. */
export function toCsvRow(
  item: SendQueueItem,
): [string, SendQueueCampaignType, string, string, string, number, SendQueueStatus] {
  return [
    item.scheduledAt,
    item.campaignType,
    item.organization,
    item.campaignTitle,
    item.sendLabel,
    item.recipients,
    item.status,
  ];
}
